import { Link } from '@tanstack/react-router'
import {
  ArrowRight,
  BellRing,
  FileSearch,
  Settings,
  ShieldCheck,
  Plug,
  Split,
  Users,
} from 'lucide-react'
import { Banner, Card } from '@shared/design-system/ui'
import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { useSession } from '@shared/lib/auth/session-store'
import { hasAdminPermission } from '@shared/lib/auth/permissions'
import { DefaultReceivingPlanPanel } from '@features/receiving-plan/ui/DefaultReceivingPlanPanel'
import {
  actionLink,
  cardHeader,
  cardIcon,
  cardText,
  grid,
  page,
  sectionCopy,
  sectionTitle,
  subtitle,
  title,
} from './ConfigPage.css'

const operationalAreas = [
  {
    to: '/config/cache-financeiro' as const,
    title: 'Cache financeiro',
    description: 'Ajuste o tempo de atualização dos indicadores financeiros deste ambiente.',
    action: 'Configurar cache financeiro',
    icon: Settings,
    permission: 'FINANCIAL_CACHE_READ',
  },
  {
    to: '/usuarios' as const,
    title: 'Usuários, cadastro e KYC',
    description:
      'Centralize cadastro, documentação, produtos e prontidão operacional de cada conta.',
    action: 'Consultar usuário',
    icon: ShieldCheck,
    permission: 'USER_READ',
  },
  {
    to: '/alertas' as const,
    title: 'Alertas e webhooks',
    description: 'Acompanhe eventos de gateway com falha, filas e outros sinais operacionais.',
    action: 'Abrir alertas',
    icon: BellRing,
    permission: 'OPS_ALERT_READ',
  },
  {
    to: '/reconciliacao' as const,
    title: 'Reconciliação',
    description: 'Investigue divergências financeiras antes de reprocessar qualquer operação.',
    action: 'Abrir reconciliação',
    icon: FileSearch,
    permission: 'RECONCILE_READ',
  },
  {
    to: '/equipe' as const,
    title: 'Equipe e permissões',
    description: 'Gerencie o acesso da equipe interna conforme a responsabilidade operacional.',
    action: 'Gerenciar equipe',
    icon: Users,
    permission: 'STAFF_MANAGE',
  },
  {
    to: '/config/checkout' as const,
    title: 'Experiência do checkout',
    description: 'Defina se o comprador vê o Checkout 1, o Checkout 2 ou o teste A/B aleatório.',
    action: 'Configurar checkout',
    icon: Split,
    permission: 'CHECKOUT_EXPERIENCE_READ',
  },
]

export function ConfigPage() {
  const crmEnabled = useFeatureFlag('crmIntegration')
  const user = useSession((state) => state.user)
  const visibleOperationalAreas = operationalAreas.filter((area) => {
    const permission =
      'permission' in area && typeof area.permission === 'string' ? area.permission : null
    return permission === null || hasAdminPermission(user, permission)
  })

  return (
    <div className={page} data-testid="config-page">
      <div>
        <h1 className={title}>Configurações da gestão</h1>
        <p className={subtitle}>Acessos e superfícies operacionais do painel interno.</p>
      </div>

      <Banner tone="info" title="Sessão administrativa protegida">
        Esta área usa somente APIs de administração. Configurações da conta do vendedor devem ser
        feitas pelo próprio titular no portal VendePay.
      </Banner>

      <Card>
        <div className={cardHeader}>
          <span className={cardIcon}>
            <Settings size={18} />
          </span>
          <div>
            <h2 className={sectionTitle}>Conta da gestão</h2>
            <p className={sectionCopy}>{user?.email ?? 'Equipe VendePay'}</p>
          </div>
        </div>
        <p className={cardText}>
          Seu acesso é limitado pelas permissões administrativas associadas a esta conta.
        </p>
      </Card>

      <div className={grid}>
        {crmEnabled && user?.isSuper === true ? (
          <Card data-testid="config-crm-card">
            <div className={cardHeader}>
              <span className={cardIcon}>
                <Plug size={18} />
              </span>
              <div>
                <h2 className={sectionTitle}>Conexão com o CRM</h2>
                <p className={sectionCopy}>Integração com o sistema de suporte da empresa.</p>
              </div>
            </div>
            <p className={cardText}>
              Configure o destino dos dados cadastrais e operacionais e acompanhe as entregas ao
              CRM.
            </p>
            <Link to="/config/crm" className={actionLink} data-testid="config-crm-open-btn">
              Gerenciar conexão com o CRM
              <ArrowRight size={16} />
            </Link>
          </Card>
        ) : null}
        {visibleOperationalAreas.map((area) => {
          const Icon = area.icon
          return (
            <Card key={area.to}>
              <div className={cardHeader}>
                <span className={cardIcon}>
                  <Icon size={18} />
                </span>
                <div>
                  <h2 className={sectionTitle}>{area.title}</h2>
                  <p className={sectionCopy}>{area.description}</p>
                </div>
              </div>
              <Link to={area.to} className={actionLink}>
                {area.action}
                <ArrowRight size={16} />
              </Link>
            </Card>
          )
        })}
      </div>
      {hasAdminPermission(user, 'USER_PLAN_MANAGE') ? <DefaultReceivingPlanPanel /> : null}
    </div>
  )
}
